import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import Container from '@components/global/Container';
import Heading from '@components/ui/Heading';
import { useLocale } from '@hooks/useLocale';
import cn from 'classnames';
import { HomepageValueProp } from '@adapters/prismic/types/brandedStorePage';
import TagManager from 'react-gtm-module';
import { localePathAbs } from '@utils/url';
import { useRef } from 'react';
import { HomepageValuePropSlice } from '@adapters/prismic/types/slices';
import { determinePageType } from '@utils/gtm/ga4/utils';
import IconWrapper from '@components/ui/Icons';
import Image from 'next/image';

import styles from './HomepageValueProps.module.scss';

export interface HomepageValueProps {
  primary: HomepageValuePropSlice['primary'];
  items: HomepageValuePropSlice['items'];
  reduceMarginTop?: boolean;
}

// Fallback for documents that predate the `value_prop_icon` field being read.
// This slice also renders on branded store and landing pages, so entries here
// keep those from losing their icon until an image is uploaded. Note it is
// keyed on the link, which means changing a tile's URL silently drops its icon
// — the reason to prefer the uploaded image.
const iconMap = {
  '/explore/shipping-info': 'valuepropsbox',
  '/explore/returns-faq': 'valuepropsreturn',
  '/explore/price-protection': 'valuepropstag',
  '/p/member-rewards': 'valuepropsmec',
  '/p/membres-mec': 'valuepropsmec',
  '/explore/returns-and-guarantee': 'valuepropscheck',
} as const;

type IconMapKey = keyof typeof iconMap;

const BrandValues = ({
  primary,
  items,
  reduceMarginTop,
}: HomepageValueProps) => {
  const locale = useLocale();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const trackValuePropCLick = (
    propClicked: HomepageValueProp,
    index: number
  ) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'user_interaction',
        event_name: 'click_value_props',
        page: {
          page_type: determinePageType(),
        },
        interaction: {
          interaction_location: `item${index + 1}`,
          link_url: localePathAbs(
            locale ?? 'en',
            propClicked.value_prop_link ?? '/'
          ),
          link_text: `${propClicked.value_prop_title} ${propClicked.value_prop_content}`,
        },
      },
    });
  };

  return (
    <div
      className={cn(styles.sectionContainer, {
        [styles.reduceMarginTop]: reduceMarginTop,
      })}
      id={primary?.id}
      ref={containerRef}
    >
      <Container>
        <div className={styles.articlesContainer}>
          {items?.map((item, i) => {
            return (
              <article
                key={`article-item-${i}`}
                className={cn(styles.articleContainer, {
                  [styles.fiveItems]: primary?.number_of_cards === 'five',
                  [styles.fourItems]: primary?.number_of_cards === 'four',
                  [styles.threeItems]: primary?.number_of_cards === 'three',
                  [styles.twoItems]: primary?.number_of_cards === 'two',
                })}
              >
                <div className={styles.imageContainer}>
                  <a
                    href={`/${locale}${item?.value_prop_link}`}
                    onClick={() => trackValuePropCLick(item, i)}
                  >
                    {item?.value_prop_icon?.url ? (
                      <Image
                        className={styles.icon}
                        src={item.value_prop_icon.url}
                        alt={item.value_prop_icon.alt ?? ''}
                        width={40}
                        height={40}
                      />
                    ) : (
                      <IconWrapper
                        classNames={styles.icon}
                        icon={
                          iconMap[item?.value_prop_link as IconMapKey] ?? ''
                        }
                        width={40}
                        height={40}
                      />
                    )}
                  </a>
                </div>
                <div>
                  <a
                    href={`/${locale}${item?.value_prop_link}`}
                    className={styles.valuePropLink}
                    onClick={() => trackValuePropCLick(item, i)}
                  >
                    {item?.value_prop_title && (
                      <Heading
                        semanticLevel="h5"
                        styleLevel="5"
                        children={item.value_prop_title}
                        className={styles.heading}
                      ></Heading>
                    )}
                    {item?.value_prop_content && (
                      <MarkdownCopy
                        text={item.value_prop_content}
                        className={styles.copy}
                      />
                    )}
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </Container>
    </div>
  );
};

export default BrandValues;
